import type { RatedProductWire } from 'shared';
import type { CartSurcharge } from '@/lib/widget-types.js';

export function defaultSurchargeSelections(tier: RatedProductWire): CartSurcharge[] {
  return tier.surcharges.map((s) => ({
    code: s.code,
    // Non-optional surcharges are always on; optional ones keep the rated default.
    selected: s.selected || !s.optional,
  }));
}

/** So changing term or re-rating doesn't silently drop add-ons and lower the price. */
export function carrySurchargeSelections(
  previous: CartSurcharge[],
  nextTier: RatedProductWire,
): CartSurcharge[] {
  return nextTier.surcharges.map((s) => {
    if (!s.optional) return { code: s.code, selected: true };
    const carried = previous.find((p) => p.code === s.code);
    return { code: s.code, selected: carried?.selected ?? s.selected };
  });
}

export interface SurchargeDetail {
  code: string;
  description: string;
  basePrice: number;
  optional: boolean;
}

/** The selected surcharges in tier order, with the price and optionality the summary
 *  view needs. `selectedSurchargeSum` totals exactly this list, so an itemised
 *  breakdown can never disagree with the amount charged. */
export function selectedSurchargeDetails(
  tier: RatedProductWire,
  selections: CartSurcharge[],
): SurchargeDetail[] {
  return tier.surcharges
    .filter((s) => selections.find((sel) => sel.code === s.code)?.selected === true)
    .map((s) => ({
      code: s.code,
      description: s.description,
      basePrice: s.base_price,
      optional: s.optional,
    }));
}

/** Codes the tier doesn't carry contribute nothing — they aren't in the detail list. */
export function selectedSurchargeSum(tier: RatedProductWire, selections: CartSurcharge[]): number {
  return selectedSurchargeDetails(tier, selections).reduce((sum, s) => sum + s.basePrice, 0);
}
